import {
  Hub,
  HubSection,
  HubSectionStatus,
  HubSectionType,
} from '@alucio/aws-beacon-amplify/src/models';

function getMergedHub(hub: Hub, hubTemplate?: Hub): Hub {
  if (!hubTemplate) {
    return hub;
  }

  const name = hubTemplate.lockName ? hubTemplate.name : hub?.name;
  let sections: HubSection[] = [];

  if (hubTemplate && Array.isArray(hubTemplate.hubSections)) {
    const templateSections = hubTemplate.hubSections.filter(section => section.status === HubSectionStatus.ACTIVE);
    const hubSections = hub && Array.isArray(hub.hubSections)
      ? hub.hubSections.filter(section => section.status === HubSectionStatus.ACTIVE)
      : [];
    const hubSectionsMap = new Map(hubSections.map(section => [section.id, section]));
    const templateSectionIds = new Set(templateSections.map(section => section.id));
    const matchedSectionIds = new Set<string>();
    const getMatchingHubSection = (templateSection: HubSection): HubSection | undefined => {
      const exactMatch = !matchedSectionIds.has(templateSection.id)
        ? hubSectionsMap.get(templateSection.id)
        : undefined;
      const fallbackMatch = hubSections.find(section => (
        !matchedSectionIds.has(section.id) &&
        !templateSectionIds.has(section.id) &&
        section.type === templateSection.type &&
        section.order === templateSection.order
      )) || hubSections.find(section => (
        !matchedSectionIds.has(section.id) &&
        !templateSectionIds.has(section.id) &&
        section.type === templateSection.type
      ));
      const matchingSection = exactMatch || fallbackMatch;
      if (matchingSection) matchedSectionIds.add(matchingSection.id);
      return matchingSection;
    };
    sections = templateSections.map(templateSection => {
      const hubSection = getMatchingHubSection(templateSection);
      if (templateSection.locked) {
        if (templateSection.type === HubSectionType.TODO_LIST) {
          const templateTodos = templateSection.toDos || [];
          const instanceTodos = hubSection?.toDos || [];
          const statusById = new Map(
            instanceTodos.map(todo => [todo.id, todo.status]),
          );
          return {
            ...templateSection,
            toDos: templateTodos.map(todo => ({
              // we are checking the status from instance because the template provides the todo items,
              // but each individual instance needs to track if the values are marked as completed or not
              // we dont get the status from the template otherwise all the instances would match the template
              ...todo,
              status: statusById.get(todo.id) ?? todo.status,
            })),
          };
        } else {
          return templateSection;
        }
      } else {
        return {
          ...(hubSection || templateSection),
          order: templateSection.order,
          // the reason we do this is because we want to know the template lock state to decide what to show in the UI
          // not a big fan of this approach because in the DB the instance will have a locked state
          // but in the UI that section may have a different status
          locked: templateSection.locked,
        };
      }
    });
  }

  return Hub.copyOf(hub, (draft) => {
    // we always get the lockName from the template
    draft.lockName = hubTemplate.lockName;
    draft.name = name;
    draft.hubSections = sections;
  });
}

export {
  getMergedHub,
};
